import { css } from '@linaria/core';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { AutoComplete, Input, Select, Space } from 'antd';
import classNames from 'classnames';
import { useState } from 'react';

import { catalogPreviewOptions } from '@lib/api/options/catalog';
import { DEFAULT_GAME_CODE, GAMES } from '@lib/games';
import { useDebouncedValue } from '@lib/hooks/use-debounced-value';
import { Game } from '@lib/types';
import { ROUTE_PATHS } from '@modules/route-paths';

import CardSearchOption from '../../../card-search-option';

interface Props {
  className?: HTMLDivElement['className'];
}

const styles = {
  search: css`
    width: 100%;

    .ant-input-group-addon {
      padding: 0;
      border: 0;
    }

    * {
      height: min-content;

      max-height: 36px;

      .ant-select-content::before {
        content: none;
      }
    }
  `,
  container: css`
    width: 100%;
    max-width: 450px;
    max-height: 36px;

    .ant-input,
    .ant-select-selector {
      background: var(--ant-color-fill-secondary);
    }
  `,
  game: css`
    width: 150px;
  `,
  searchInput: css`
    min-width: 275px;

    @media (max-width: 768px) {
      width: 100%;
      min-width: 150px;
    }
  `,
  dropdown: css`
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;

    .ant-select-item-option-content {
      touch-action: pan-y;
    }
  `,
};

const CARD_PREVIEW_DELAY = 300;
const MINIMUM_CARD_PREVIEW_LENGTH = 2;

const GlobalCardSearch = ({ className }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();

  const routeGameCode = location.pathname.match(
    /^\/([^/]+)\/cards(?:\/|$)/,
  )?.[1];

  const [gameCode, setGameCode] = useState<Game['slug']>(
    routeGameCode ?? DEFAULT_GAME_CODE,
  );
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebouncedValue(search.trim(), CARD_PREVIEW_DELAY);

  const { data: preview = [], isFetching } = useQuery(
    catalogPreviewOptions(debouncedSearch, gameCode),
  );

  const onSearchCard = (search: string) => {
    navigate({
      to: ROUTE_PATHS.CARDS,
      params: { gameCode },
      search: { search: search.trim() },
    }).catch(() => undefined);
  };

  const onSelectGame = (nextGameCode: string) => {
    setGameCode(nextGameCode);
    navigate({
      to: ROUTE_PATHS.CARDS,
      params: { gameCode: nextGameCode },
      search: {},
    }).catch(() => undefined);
  };

  const onSelectCard = (cardSlug: string) => {
    setSearch('');
    navigate({
      to: ROUTE_PATHS.CARD_DETAILS,
      params: { cardSlug, gameCode },
    }).catch(() => undefined);
  };

  const options = preview.map((card) => ({
    value: card.slug,
    label: (
      <CardSearchOption
        imageUrl={card.imageUrl}
        nameEn={card.nameEn}
        namePt={card.namePt}
      />
    ),
  }));

  return (
    <Space.Compact className={classNames(styles.container, className)}>
      <Select
        className={styles.game}
        aria-label="Selecionar TCG"
        value={gameCode}
        options={GAMES.map((game) => ({
          label: game.name,
          value: game.slug,
        }))}
        onChange={onSelectGame}
        variant="filled"
      />
      <AutoComplete
        className={styles.search}
        value={search}
        options={options}
        notFoundContent={
          debouncedSearch.length >= MINIMUM_CARD_PREVIEW_LENGTH && !isFetching
            ? 'Nenhuma carta encontrada'
            : null
        }
        onChange={setSearch}
        onSelect={onSelectCard}
        classNames={{
          popup: {
            root: styles.dropdown,
          },
        }}
      >
        <Input.Search
          className={styles.searchInput}
          aria-label="Buscar cartas no marketplace"
          loading={isFetching}
          placeholder="Buscar carta"
          onSearch={onSearchCard}
        />
      </AutoComplete>
    </Space.Compact>
  );
};

export default GlobalCardSearch;
